import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ClipboardList, FileText, Plus } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ProposalStatusBadge } from "@/components/common/ProposalStatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RoleGate } from "@/components/auth/RoleGate";
import { proposalsService } from "@/services/proposals.service";
import { customersService } from "@/services/customers.service";
import {
    PROPOSAL_STATUSES, PROPOSAL_STATUS_LABELS, formatBRL,
} from "@/config/constants";
import { formatDate } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";

export default function ProposalListPage() {
    const navigate = useNavigate();
    const { can } = usePermission();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [customerId, setCustomerId] = useState("");

    const dashboard = useQuery({
        queryKey: ["proposals-dashboard"],
        queryFn: () => proposalsService.dashboard(),
        refetchInterval: 30000,
    });

    const customers = useQuery({
        queryKey: ["customers-lite"],
        queryFn: () => customersService.list({ pageSize: 100 }),
    });

    const list = useQuery({
        queryKey: ["proposals", { page, search, status, customerId }],
        queryFn: () => proposalsService.list({
            page, pageSize: 20, search,
            status: status || undefined,
            customer_id: customerId || undefined,
            sortBy: "created_at", sortOrder: "desc",
        }),
    });

    const columns = [
        {
            key: "number", header: "Proposta",
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <FileText className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="font-mono text-sm font-medium">{r.number}</div>
                        <div className="text-xs text-muted-foreground">
                            {r.work_order_number ? `OS ${r.work_order_number}` : "Proposta avulsa"}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: "customer", header: "Cliente",
            cell: (r) => (
                <div>
                    <div className="text-sm font-medium truncate max-w-[240px]">
                        {r.customer_snapshot?.legal_name || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-[240px]">
                        {r.customer_snapshot?.trade_name}
                    </div>
                </div>
            ),
        },
        {
            key: "asset", header: "Equipamento",
            cell: (r) => (
                <div>
                    <div className="font-mono text-xs">{r.asset_snapshot?.internal_code}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                        {r.asset_snapshot?.manufacturer} {r.asset_snapshot?.model}
                    </div>
                </div>
            ),
        },
        {
            key: "status", header: "Status",
            cell: (r) => <ProposalStatusBadge value={r.status} />,
        },
        {
            key: "total", header: "Total",
            cell: (r) => (
                <span className="font-mono text-sm font-medium">
                    {formatBRL((r.totals || {}).grand_total)}
                </span>
            ),
        },
        {
            key: "issue_date", header: "Emissão",
            cell: (r) => (
                <span className="text-xs text-muted-foreground">
                    {r.issue_date ? formatDate(r.issue_date) : "—"}
                </span>
            ),
        },
    ];

    const pending = dashboard.data?.workshops_pending_proposal || 0;
    const filtersActive = [];
    if (status) filtersActive.push(`Status: ${PROPOSAL_STATUS_LABELS[status] || status}`);
    if (customerId) filtersActive.push("Cliente filtrado");

    return (
        <div className="space-y-6" data-testid="proposal-list-page">
            <PageHeader
                title="Propostas Comerciais"
                description="Emissão e acompanhamento das propostas — crie manualmente ou a partir de uma oficina."
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Propostas" },
                ]}
                actions={
                    <RoleGate resource="commercial_proposals" action="create">
                        <Button
                            onClick={() => navigate("/proposals/new")}
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            data-testid="proposals-new-button"
                        >
                            <Plus className="mr-2 h-4 w-4" /> Nova proposta
                        </Button>
                    </RoleGate>
                }
            />

            {pending > 0 && (
                <Card className="border-yellow-500/40 bg-yellow-50/60 dark:bg-yellow-950/20" data-testid="proposals-pending-alert">
                    <CardContent className="flex flex-col items-start justify-between gap-3 p-4 sm:flex-row sm:items-center">
                        <div className="flex items-start gap-3">
                            <div className="grid h-9 w-9 place-items-center rounded-md bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300">
                                <AlertCircle className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold">
                                    {pending} diagnóstico(s) aguardando proposta comercial
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Olá! Há oficinas com o diagnóstico técnico finalizado esperando uma proposta ser criada.
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="self-stretch sm:self-auto"
                            onClick={() => navigate("/workshop?status=aguardando_proposta")}
                            data-testid="proposals-open-workshop-button"
                        >
                            Ver na Oficina <ClipboardList className="ml-2 h-3.5 w-3.5" />
                        </Button>
                    </CardContent>
                </Card>
            )}

            <DataTable
                dataTestId="proposals"
                columns={columns}
                query={list.data}
                loading={list.isLoading}
                onSearch={(t) => { setPage(1); setSearch(t); }}
                onPageChange={setPage}
                onRowClick={(r) => navigate(`/proposals/${r.id}`)}
                emptyState={{
                    title: "Nenhuma proposta ainda",
                    description:
                        "Crie uma nova proposta manualmente ou gere a partir de um diagnóstico finalizado na Oficina.",
                    cta: can("commercial_proposals", "create") ? "Nova proposta" : undefined,
                }}
                onCreate={can("commercial_proposals", "create") ? () => navigate("/proposals/new") : null}
                createLabel="Nova proposta"
                filtersActive={filtersActive}
                toolbarLeft={
                    <>
                        <Select
                            value={status || "__all__"}
                            onValueChange={(v) => { setPage(1); setStatus(v === "__all__" ? "" : v); }}
                        >
                            <SelectTrigger className="h-9 w-56" data-testid="proposals-status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos status</SelectItem>
                                {PROPOSAL_STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        <span className="flex items-center gap-2">
                                            <span className={`inline-block h-2 w-2 rounded-full ${s.dotClass}`} />
                                            {s.label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={customerId || "__all__"}
                            onValueChange={(v) => { setPage(1); setCustomerId(v === "__all__" ? "" : v); }}
                        >
                            <SelectTrigger className="h-9 w-56" data-testid="proposals-customer-filter">
                                <SelectValue placeholder="Cliente" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos clientes</SelectItem>
                                {(customers.data?.items || []).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />
        </div>
    );
}
